<template>
  <div class="write-approval-card" :class="`write-approval-card--${approval.status}`">
    <!-- 头部 -->
    <div class="write-approval-card__header">
      <span class="write-approval-card__icon">
        <svg v-if="approval.status === 'pending'" width="16" height="16" viewBox="0 0 24 24" fill="none">
          <circle cx="12" cy="12" r="10" stroke="#0052D9" stroke-width="1.8"/>
          <path d="M9.5 9a2.5 2.5 0 0 1 5 0c0 1.5-2.5 2-2.5 3.5" stroke="#0052D9" stroke-width="1.8" stroke-linecap="round"/>
          <circle cx="12" cy="17" r="1" fill="#0052D9"/>
        </svg>
        <svg v-else-if="approval.status === 'approved'" width="16" height="16" viewBox="0 0 24 24" fill="none">
          <circle cx="12" cy="12" r="10" fill="#07c160" opacity="0.12"/>
          <path d="M7 12.5l3.5 3.5 6.5-7" stroke="#07c160" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
        <svg v-else-if="approval.status === 'denied'" width="16" height="16" viewBox="0 0 24 24" fill="none">
          <circle cx="12" cy="12" r="10" fill="#e34d59" opacity="0.12"/>
          <path d="M8 8l8 8M16 8l-8 8" stroke="#e34d59" stroke-width="2" stroke-linecap="round"/>
        </svg>
        <svg v-else width="16" height="16" viewBox="0 0 24 24" fill="none">
          <circle cx="12" cy="12" r="10" stroke="#86909c" stroke-width="1.8"/>
          <path d="M12 7v5l3 3" stroke="#86909c" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
      </span>
      <span class="write-approval-card__title">操作确认</span>
      <span class="write-approval-card__status-badge">{{ statusBadge }}</span>
    </div>

    <!-- 操作摘要 -->
    <div class="write-approval-card__body">
      <div class="write-approval-card__action-label">{{ approval.actionLabel }}</div>

      <!-- 问卷结构化卡片 -->
      <template v-if="approval.toolName === 'manage_questionnaire_template' && approval.questionnaireData">
        <div class="questionnaire-card">
          <!-- 问卷基本信息 -->
          <div class="questionnaire-card__meta">
            <span class="questionnaire-card__meta-label">操作类型：</span>
            <span class="questionnaire-card__meta-value">{{ approval.questionnaireData.action === 'create' ? '新建问卷模板' : '编辑问卷模板' }}</span>
          </div>
          <div class="questionnaire-card__meta">
            <span class="questionnaire-card__meta-label">问卷名称：</span>
            <span class="questionnaire-card__meta-value questionnaire-card__meta-value--name">{{ approval.questionnaireData.questionnaireName }}</span>
          </div>
          <!-- 题目列表 -->
          <div class="questionnaire-card__questions-header">
            题目列表（共 {{ approval.questionnaireData.questions.length }} 题）
          </div>
          <div class="questionnaire-card__questions">
            <div
              v-for="(q, idx) in approval.questionnaireData.questions"
              :key="idx"
              class="questionnaire-card__question"
            >
              <!-- 题目标题行 -->
              <div class="questionnaire-card__question-title">
                <span class="questionnaire-card__question-index">{{ idx + 1 }}</span>
                <span class="questionnaire-card__type-badge">{{ q.typeLabel }}</span>
                <span class="questionnaire-card__question-text">{{ q.title }}</span>
              </div>
              <!-- 选项列表 -->
              <div v-if="q.options && q.options.length" class="questionnaire-card__options">
                <div v-for="(opt, oi) in q.options" :key="oi" class="questionnaire-card__option-block">
                  <!-- L1 选项 -->
                  <div class="questionnaire-card__option">
                    <span class="questionnaire-card__option-dot">·</span>
                    <span class="questionnaire-card__option-value">{{ opt.value }}</span>
                  </div>
                  <!-- L2 子题目 -->
                  <div v-if="opt.subQuestions && opt.subQuestions.length" class="questionnaire-card__sub-questions">
                    <div v-for="(sq2, si2) in opt.subQuestions" :key="si2" class="questionnaire-card__sub-question">
                      <div class="questionnaire-card__sub-question-title">
                        <span class="questionnaire-card__sub-arrow">↳</span>
                        <span class="questionnaire-card__type-badge questionnaire-card__type-badge--sub">{{ sq2.typeLabel }}</span>
                        <span class="questionnaire-card__sub-question-text">{{ sq2.title }}</span>
                      </div>
                      <!-- L2 子题目的选项 -->
                      <div v-if="sq2.options && sq2.options.length" class="questionnaire-card__options questionnaire-card__options--sub">
                        <div v-for="(opt2, oi2) in sq2.options" :key="oi2" class="questionnaire-card__option-block">
                          <div class="questionnaire-card__option">
                            <span class="questionnaire-card__option-dot">·</span>
                            <span class="questionnaire-card__option-value">{{ opt2.value }}</span>
                          </div>
                          <!-- L3 子题目 -->
                          <div v-if="opt2.subQuestions && opt2.subQuestions.length" class="questionnaire-card__sub-questions">
                            <div v-for="(sq3, si3) in opt2.subQuestions" :key="si3" class="questionnaire-card__sub-question">
                              <div class="questionnaire-card__sub-question-title">
                                <span class="questionnaire-card__sub-arrow">↳</span>
                                <span class="questionnaire-card__type-badge questionnaire-card__type-badge--sub">{{ sq3.typeLabel }}</span>
                                <span class="questionnaire-card__sub-question-text">{{ sq3.title }}</span>
                              </div>
                              <!-- L3 子题目的选项 -->
                              <div v-if="sq3.options && sq3.options.length" class="questionnaire-card__options questionnaire-card__options--sub">
                                <div v-for="(opt3, oi3) in sq3.options" :key="oi3" class="questionnaire-card__option-block">
                                  <div class="questionnaire-card__option">
                                    <span class="questionnaire-card__option-dot">·</span>
                                    <span class="questionnaire-card__option-value">{{ opt3.value }}</span>
                                  </div>
                                  <!-- L4 子题目 -->
                                  <div v-if="opt3.subQuestions && opt3.subQuestions.length" class="questionnaire-card__sub-questions">
                                    <div v-for="(sq4, si4) in opt3.subQuestions" :key="si4" class="questionnaire-card__sub-question">
                                      <div class="questionnaire-card__sub-question-title">
                                        <span class="questionnaire-card__sub-arrow">↳</span>
                                        <span class="questionnaire-card__type-badge questionnaire-card__type-badge--sub">{{ sq4.typeLabel }}</span>
                                        <span class="questionnaire-card__sub-question-text">{{ sq4.title }}</span>
                                      </div>
                                      <!-- L4 子题目的选项（最深一级，不再有子题目） -->
                                      <div v-if="sq4.options && sq4.options.length" class="questionnaire-card__options questionnaire-card__options--sub">
                                        <div v-for="(opt4, oi4) in sq4.options" :key="oi4" class="questionnaire-card__option-block">
                                          <div class="questionnaire-card__option">
                                            <span class="questionnaire-card__option-dot">·</span>
                                            <span class="questionnaire-card__option-value">{{ opt4.value }}</span>
                                          </div>
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </template>

      <!-- 通用文本摘要 -->
      <div v-else class="write-approval-card__summary">{{ approval.paramsSummary }}</div>
    </div>

    <!-- 操作按钮（仅 pending 状态显示，且本地未操作时） -->
    <div v-if="approval.status === 'pending' && localStatus === 'pending'" class="write-approval-card__footer">
      <button
        class="write-approval-card__btn write-approval-card__btn--cancel"
        :disabled="submitting"
        @click="onDecision('deny')"
      >
        取消
      </button>
      <button
        class="write-approval-card__btn write-approval-card__btn--confirm"
        :disabled="submitting"
        @click="onDecision('allow-once')"
      >
        <span v-if="submitting" class="write-approval-card__btn-spinner"></span>
        <span v-else>确认执行</span>
      </button>
    </div>
    <!-- 本地已操作，等待后端确认时的过渡状态 -->
    <div v-else-if="approval.status === 'pending' && localStatus !== 'pending'" class="write-approval-card__footer write-approval-card__footer--done">
      <span class="write-approval-card__done-text">
        {{ localStatus === 'allow-once' ? '✓ 已确认，执行中…' : '✗ 已取消' }}
      </span>
    </div>
  </div>
</template>

<script>
import { computed, ref } from '@vue/composition-api';

export default {
  name: 'WriteApprovalCard',
  props: {
    approval: {
      type: Object,
      required: true,
    },
  },
  setup(props, { emit }) {
    // 本地状态：用于点击后立即给用户反馈，不等后端回包
    const localStatus = ref('pending'); // 'pending' | 'allow-once' | 'deny'
    const submitting = ref(false);

    const statusBadge = computed(() => {
      // 后端推送的 expired/approved/denied 状态优先（不被本地乐观状态覆盖）
      if (props.approval.status === 'expired' || props.approval.status === 'approved' || props.approval.status === 'denied') {
        const map = { pending: '待确认', approved: '已确认', denied: '已取消', expired: '已超时' };
        return map[props.approval.status] || '';
      }
      // pending 状态下，用本地乐观状态给用户即时反馈
      const effectiveStatus = localStatus.value !== 'pending'
        ? (localStatus.value === 'allow-once' ? 'approved' : 'denied')
        : props.approval.status;
      const map = { pending: '待确认', approved: '已确认', denied: '已取消', expired: '已超时' };
      return map[effectiveStatus] || '';
    });

    function onDecision(decision) {
      if (submitting.value) return;
      // 如果后端已推送超时/完成状态，阻止点击（竞态保护）
      if (props.approval.status !== 'pending') return;
      submitting.value = true;
      localStatus.value = decision;
      emit('decision', props.approval.approvalId, decision);
    }

    return { statusBadge, localStatus, submitting, onDecision };
  },
};
</script>

<style scoped>
.write-approval-card {
  background: #fff;
  border-radius: 12px;
  border: 1.5px solid #d8e3f5;
  overflow: hidden;
  margin: 4px 0;
  box-shadow: 0 2px 8px rgba(0, 82, 217, 0.08);
  width: 100%;
}
.write-approval-card--approved { border-color: #b7ebc8; box-shadow: 0 2px 8px rgba(7, 193, 96, 0.08); }
.write-approval-card--denied,
.write-approval-card--expired { border-color: #e8ecf0; box-shadow: none; opacity: 0.75; }

.write-approval-card__header {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 14px 8px;
  border-bottom: 1px solid #f0f2f5;
}
.write-approval-card__icon { display: flex; align-items: center; flex-shrink: 0; }
.write-approval-card__title { font-size: 13px; font-weight: 600; color: #1a202c; flex: 1; }
.write-approval-card__status-badge {
  font-size: 11px;
  padding: 2px 7px;
  border-radius: 999px;
  background: #e8f0fe;
  color: #0052D9;
  font-weight: 500;
}
.write-approval-card--approved .write-approval-card__status-badge { background: #e8f8ef; color: #07c160; }
.write-approval-card--denied .write-approval-card__status-badge { background: #fff2f0; color: #e34d59; }
.write-approval-card--expired .write-approval-card__status-badge { background: #f5f7fa; color: #86909c; }

.write-approval-card__body { padding: 10px 14px; display: flex; flex-direction: column; gap: 6px; }
.write-approval-card__action-label { font-size: 13px; font-weight: 600; color: #1a202c; }
.write-approval-card__summary {
  font-size: 12px;
  color: #4a5568;
  line-height: 1.7;
  white-space: pre-wrap;
  word-break: break-all;
  max-height: 160px;
  overflow-y: auto;
  scrollbar-width: thin;
  scrollbar-color: #d0d7e3 transparent;
}
.write-approval-card__summary::-webkit-scrollbar { width: 4px; }
.write-approval-card__summary::-webkit-scrollbar-track { background: transparent; }
.write-approval-card__summary::-webkit-scrollbar-thumb { background: #d0d7e3; border-radius: 2px; }

/* 问卷结构化卡片样式 */
.questionnaire-card {
  display: flex;
  flex-direction: column;
  gap: 4px;
}
.questionnaire-card__meta {
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 12px;
  line-height: 1.6;
}
.questionnaire-card__meta-label {
  color: #86909c;
  flex-shrink: 0;
}
.questionnaire-card__meta-value {
  color: #4a5568;
}
.questionnaire-card__meta-value--name {
  font-weight: 600;
  color: #1a202c;
}
.questionnaire-card__questions-header {
  font-size: 12px;
  color: #86909c;
  margin-top: 6px;
  padding-bottom: 4px;
  border-bottom: 1px dashed #e8ecf0;
}
.questionnaire-card__questions {
  display: flex;
  flex-direction: column;
  gap: 6px;
  max-height: 220px;
  overflow-y: auto;
  scrollbar-width: thin;
  scrollbar-color: #d0d7e3 transparent;
  padding-right: 2px;
}
.questionnaire-card__questions::-webkit-scrollbar { width: 4px; }
.questionnaire-card__questions::-webkit-scrollbar-track { background: transparent; }
.questionnaire-card__questions::-webkit-scrollbar-thumb { background: #d0d7e3; border-radius: 2px; }
.questionnaire-card__question {
  background: #f7f9fc;
  border-radius: 6px;
  padding: 6px 8px;
  display: flex;
  flex-direction: column;
  gap: 3px;
}
.questionnaire-card__question-title {
  display: flex;
  align-items: flex-start;
  gap: 5px;
  font-size: 12px;
  color: #1a202c;
  line-height: 1.5;
}
.questionnaire-card__question-index {
  flex-shrink: 0;
  width: 16px;
  height: 16px;
  background: #0052D9;
  color: #fff;
  border-radius: 50%;
  font-size: 10px;
  font-weight: 600;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-top: 1px;
}
.questionnaire-card__type-badge {
  flex-shrink: 0;
  font-size: 10px;
  padding: 1px 5px;
  border-radius: 4px;
  background: #e8f0fe;
  color: #0052D9;
  font-weight: 500;
  margin-top: 1px;
}
.questionnaire-card__question-text {
  flex: 1;
  word-break: break-all;
}
.questionnaire-card__options {
  display: flex;
  flex-direction: column;
  gap: 2px;
  padding-left: 21px;
}
.questionnaire-card__option-block {
  display: flex;
  flex-direction: column;
  gap: 2px;
}
.questionnaire-card__option {
  display: flex;
  align-items: center;
  gap: 3px;
  font-size: 11px;
  color: #4a5568;
  line-height: 1.6;
}
.questionnaire-card__option-dot {
  color: #c0c7d0;
  flex-shrink: 0;
}
.questionnaire-card__option-value {
  flex: 1;
  word-break: break-all;
}
/* 子题目容器 */
.questionnaire-card__sub-questions {
  margin-left: 12px;
  padding-left: 8px;
  border-left: 2px solid #e8f0fe;
  display: flex;
  flex-direction: column;
  gap: 3px;
  margin-top: 2px;
  margin-bottom: 2px;
}
.questionnaire-card__sub-questions--l3 {
  margin-left: 8px;
  border-left-color: #f0f2f5;
}
.questionnaire-card__sub-question {
  display: flex;
  flex-direction: column;
  gap: 2px;
}
.questionnaire-card__sub-question-title {
  display: flex;
  align-items: flex-start;
  gap: 4px;
  font-size: 11px;
  color: #4a5568;
  line-height: 1.5;
}
.questionnaire-card__sub-arrow {
  color: #0052D9;
  flex-shrink: 0;
  font-size: 10px;
  margin-top: 1px;
}
.questionnaire-card__type-badge--sub {
  background: #f0f4ff;
  color: #4a7fd4;
  font-size: 9px;
  padding: 0 4px;
}
.questionnaire-card__sub-question-text {
  flex: 1;
  word-break: break-all;
  color: #2d3748;
}
/* 子题目内的选项缩进 */
.questionnaire-card__options--sub {
  padding-left: 14px;
}

.write-approval-card__footer { display: flex; gap: 8px; padding: 8px 14px 12px; }
.write-approval-card__btn {
  flex: 1;
  height: 34px;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  border: none;
  transition: all 0.15s ease;
}
.write-approval-card__btn--cancel { background: #f5f7fa; color: #4a5568; border: 1px solid #e2e8f0; }
.write-approval-card__btn--cancel:hover { background: #eef0f3; }
.write-approval-card__btn--confirm {
  background: linear-gradient(135deg, #0052D9 0%, #2979FF 100%);
  color: #fff;
  box-shadow: 0 2px 6px rgba(0, 82, 217, 0.3);
}
.write-approval-card__btn--confirm:hover:not(:disabled) { opacity: 0.9; transform: translateY(-1px); }
.write-approval-card__btn:disabled { opacity: 0.6; cursor: not-allowed; transform: none !important; }

/* 按钮 loading spinner */
.write-approval-card__btn-spinner {
  display: inline-block;
  width: 12px;
  height: 12px;
  border: 2px solid rgba(255,255,255,0.4);
  border-top-color: #fff;
  border-radius: 50%;
  animation: btn-spin 0.6s linear infinite;
  vertical-align: middle;
}
@keyframes btn-spin {
  to { transform: rotate(360deg); }
}

/* 已操作后的过渡提示 */
.write-approval-card__footer--done {
  justify-content: center;
  padding: 10px 14px 12px;
}
.write-approval-card__done-text {
  font-size: 12px;
  color: #86909c;
}
</style>
